import { CaretDown, CaretUp, DotsSixVertical } from "@phosphor-icons/react";
import { InstrumentTypeResponse } from "../../codegen-api";
import { ICON_COLORS } from "../../constants/design/colors";
import { IMarket } from "../../contexts/MarketContext";
import usePointerSort from "../../hooks/usePointerSort";
import { AssetResponse } from "../../utils/asset";
import MarketsTable, { IMarketData } from "./MarketsTable";
import {
  Section,
  SectionContentWrapper,
  SectionHeader,
  Title,
} from "./style";
import { ISectionData, MarketFiltersEnum } from "./CommandModal.types";

type ICommandSectionProps = {
  s: ISectionData;
  sIndex: number;
  collapsedSections: Record<string, boolean>;
  sectionSort: ReturnType<typeof usePointerSort>;
  toggleSectionCollapsed: (name: string) => void;
  isMobileScreen: boolean;
  highlightedMarketIndex: {
    sectionIndex: number;
    dataIndex: number;
  };
  onRowClick: (asset: AssetResponse, derivative: InstrumentTypeResponse) => void;
  onWatchlistClick: (
    asset: AssetResponse,
    derivative: InstrumentTypeResponse
  ) => void;
  marketFilter: MarketFiltersEnum;
  watchlist: IMarket[];
  updateWatchlist: (list: IMarket[]) => void;
};

function CommandSection({
  s,
  sIndex,
  collapsedSections,
  sectionSort,
  toggleSectionCollapsed,
  isMobileScreen,
  highlightedMarketIndex,
  onRowClick,
  onWatchlistClick,
  marketFilter,
  watchlist,
  updateWatchlist,
}: ICommandSectionProps) {
  const isCollapsed = !!collapsedSections[s.name];
  const isDragging = sectionSort.dragIndex === sIndex;
  const isDragOver =
    sectionSort.overIndex === sIndex &&
    sectionSort.dragIndex !== sIndex;
  return (
    <Section
      {...sectionSort.getItemProps(sIndex)}
      dragging={isDragging}
      dragOver={isDragOver}
    >
      <SectionHeader
        type="button"
        onClick={() => toggleSectionCollapsed(s.name)}
        aria-expanded={!isCollapsed}
      >
        <Title>
          <DotsSixVertical
            size={14}
            color={ICON_COLORS.three}
            style={{ cursor: "grab", marginRight: 6 }}
          />
          {s.name}
        </Title>
        {isCollapsed ? (
          <CaretDown size={14} color={ICON_COLORS.two} />
        ) : (
          <CaretUp size={14} color={ICON_COLORS.two} />
        )}
      </SectionHeader>
      <SectionContentWrapper
        collapsed={isCollapsed}
        aria-hidden={isCollapsed}
      >
        <MarketsTable
          isMobile={isMobileScreen}
          type={s.instrumentType as any}
          marketsData={s.data as any}
          highlightedIndex={
            highlightedMarketIndex?.sectionIndex === sIndex
              ? highlightedMarketIndex?.dataIndex
              : undefined
          }
          onRowClick={(row: IMarketData) =>
            onRowClick(row.asset, s.instrumentType)
          }
          onWatchlist={(row: IMarketData) =>
            onWatchlistClick(row.asset, s.instrumentType)
          }
          enableReorder={
            marketFilter === MarketFiltersEnum.WATCHLIST
          }
          onReorder={(fromIdx, toIdx) => {
            // Reorder watchlist entries of this section's instrument
            // type. The visible rows in the table are exactly the
            // watchlist items for this derivative, in the same
            // order, so the indices map directly.
            const sameType: IMarket[] = [];
            const others: IMarket[] = [];
            watchlist.forEach((w) => {
              if (w.derivative === s.instrumentType) {
                sameType.push(w);
              } else {
                others.push(w);
              }
            });
            const [moved] = sameType.splice(fromIdx, 1);
            sameType.splice(toIdx, 0, moved);
            updateWatchlist([...sameType, ...others]);
          }}
        />
      </SectionContentWrapper>
    </Section>
  );
}

export default CommandSection;
